import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLES = ["admin", "manager", "accountant"];

function Field({ label, children }) {
  return <div><Label className="label-caps mb-2 block">{label}</Label>{children}</div>;
}

export default function Users() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "accountant" });

  const load = () => api.get("/auth/users").then((r) => setRows(r.data));
   
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/auth/users", form);
      toast.success("User created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "accountant" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete user?")) return;
    try { await api.delete(`/auth/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        title="Team"
        subtitle="Manage users, roles and access."
        actions={
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button data-testid="create-user-button" className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white">
                <Plus size={16} className="mr-2" /> Invite User
              </Button>
            </SheetTrigger>
            <SheetContent className="w-[420px]">
              <SheetHeader><SheetTitle className="font-display">Create user</SheetTitle></SheetHeader>
              <div className="space-y-4 mt-6">
                <Field label="Name"><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                <Field label="Email"><Input type="email" data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                <Field label="Temporary password"><Input type="text" data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
                <Field label="Role">
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Button onClick={submit} data-testid="save-user-button" className="w-full rounded-sm bg-[#0F172A] hover:bg-slate-800">Create User</Button>
              </div>
            </SheetContent>
          </Sheet>
        }
      />
      <div className="p-8">
        {rows.length === 0 ? (
          <EmptyState title="Just you here." description="Invite team members to collaborate." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-sm" data-testid="users-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="label-caps">Name</TableHead>
                  <TableHead className="label-caps">Email</TableHead>
                  <TableHead className="label-caps">Role</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-slate-600">{u.email}</TableCell>
                    <TableCell><span className="text-xs uppercase tracking-wider font-semibold text-[#EA580C]">{u.role}</span></TableCell>
                    <TableCell><button onClick={() => del(u.id)} className="text-slate-400 hover:text-red-600 p-1"><Trash size={14} /></button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
